import { useCallback, useEffect, useRef, useState } from 'react';

// ─── helpers ────────────────────────────────────────────────────────────────

/**
 * Converts a 24-hour hour value (0-23) into 12-hour parts.
 * @param {number} h24
 * @returns {{ h12: number, ampm: 'AM'|'PM' }}
 */
const to12 = (h24) => ({
  h12: h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24,
  ampm: h24 >= 12 ? 'PM' : 'AM',
});

/**
 * Converts 12-hour parts back to a 24-hour hour value.
 * @param {number} h12  1-12
 * @param {'AM'|'PM'} ampm
 * @returns {number} 0-23
 */
const to24 = (h12, ampm) => {
  if (ampm === 'AM') return h12 === 12 ? 0 : h12;
  return h12 === 12 ? 12 : h12 + 12;
};

/**
 * Formats a Date as "h:mm AM/PM".
 * @param {Date} date
 * @returns {string}
 */
export const formatTime = (date) => {
  if (!date || !(date instanceof Date) || isNaN(date.getTime())) return '';
  const { h12, ampm } = to12(date.getHours());
  const mm = String(date.getMinutes()).padStart(2, '0');
  return `${h12}:${mm} ${ampm}`;
};

/**
 * Parses a raw input string into hours (0-23) and minutes, or returns null.
 * Handles:
 *   24-hr: "1400", "14 00", "14:00"  (hours 0 or 13-23)
 *   12-hr: "2:30 PM", "2:30PM", "2:30 pm"
 * @param {string} raw
 * @returns {{ h24: number, minutes: number }|null}
 */
export const parseTimeInput = (raw) => {
  if (!raw) return null;
  const t = raw.trim();

  // 12-hour with AM/PM
  const match12 = t.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (match12) {
    const h = parseInt(match12[1], 10);
    const m = parseInt(match12[2], 10);
    const ap = match12[3].toUpperCase();
    if (h < 1 || h > 12 || m > 59) return null;
    return { h24: to24(h, ap), minutes: m };
  }

  // 24-hour: "1400", "14 00", "14:00", "0900"
  const match24 = t.match(/^(\d{1,2})\s*:?\s*(\d{2})$/);
  if (match24) {
    const hourStr = match24[1];
    const h = parseInt(hourStr, 10);
    const m = parseInt(match24[2], 10);
    if (h > 23 || m > 59) return null;
    // Hours 1-11 without AM/PM are ambiguous UNLESS written with a leading zero
    // (e.g. "0900" = 9:00 AM, but "900" is rejected as ambiguous).
    // h=0  (0000) = midnight (12:00 AM) — always unambiguous
    // h=12 (1200) = noon    (12:00 PM) — always unambiguous in 24hr
    // h=13-23     = PM range            — always unambiguous
    const hasLeadingZero = hourStr.startsWith('0');
    if (h !== 0 && h !== 12 && h < 13 && !hasLeadingZero) return null;
    return { h24: h, minutes: m };
  }

  return null;
};

// ─── hook ───────────────────────────────────────────────────────────────────

/**
 * Encapsulates all logic for the custom 12-hour time picker.
 *
 * @param {Object}   params
 * @param {Date|null} params.value       - Current selected Date (browser-local).
 * @param {Function}  params.onChange    - Called with a PrimeReact-shaped event
 *                                         { value: Date, target: { value: Date } }
 *                                         whenever the selected time changes.
 * @param {string}   [params.id]
 * @param {string}   [params.name]
 */
const useTimePicker = ({ value, onChange, id, name }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Ref to the wrapper <span> so click-outside detection can ignore it.
  const wrapperRef = useRef(null);
  // Ref to the popup <div> portal (set by CustomTimePicker via callback).
  const popupRef = useRef(null);

  // ── derived display values ────────────────────────────────────────────────

  const isValidDate = value instanceof Date && !isNaN(value.getTime());
  const rawHour = isValidDate ? value.getHours() : 12; // default: 12:00 PM
  const { h12, ampm } = to12(rawHour);
  const minutes = isValidDate ? value.getMinutes() : 0;

  // ── emit helper ───────────────────────────────────────────────────────────

  const emit = useCallback(
    (h24, min) => {
      const d = new Date();
      d.setHours(h24, min, 0, 0);
      onChange({ value: d, target: { id, name, value: d } });
    },
    [onChange, id, name]
  );

  // ── hour controls ─────────────────────────────────────────────────────────

  const incrementHour = useCallback(() => {
    const next12 = h12 === 12 ? 1 : h12 + 1;
    emit(to24(next12, ampm), minutes);
  }, [h12, ampm, minutes, emit]);

  const decrementHour = useCallback(() => {
    const prev12 = h12 === 1 ? 12 : h12 - 1;
    emit(to24(prev12, ampm), minutes);
  }, [h12, ampm, minutes, emit]);

  // ── minute controls (30-min boundary snap) ────────────────────────────────

  const incrementMinute = useCallback(() => {
    if (minutes < 30) {
      // snap to :30
      emit(to24(h12, ampm), 30);
    } else {
      // snap to :00 of next hour
      const next12 = h12 === 12 ? 1 : h12 + 1;
      const nextAmpm = h12 === 11 ? (ampm === 'AM' ? 'PM' : 'AM') : ampm;
      emit(to24(next12, nextAmpm), 0);
    }
  }, [minutes, h12, ampm, emit]);

  const decrementMinute = useCallback(() => {
    if (minutes > 0 && minutes <= 30) {
      // snap to :00
      emit(to24(h12, ampm), 0);
    } else {
      // snap to :30 of previous hour
      const prev12 = h12 === 1 ? 12 : h12 - 1;
      const prevAmpm = h12 === 12 ? (ampm === 'AM' ? 'PM' : 'AM') : ampm;
      emit(to24(prev12, prevAmpm), 30);
    }
  }, [minutes, h12, ampm, emit]);

  // ── AM/PM toggle ──────────────────────────────────────────────────────────

  const toggleAmPm = useCallback(() => {
    const nextAmpm = ampm === 'AM' ? 'PM' : 'AM';
    emit(to24(h12, nextAmpm), minutes);
  }, [ampm, h12, minutes, emit]);

  // ── text input blur ───────────────────────────────────────────────────────

  /**
   * Called when the text input loses focus.
   * Parses the raw text; on success emits the new value,
   * on failure the input reverts to the formatted value via the
   * `inputText` it already displays (controlled by the component).
   * @param {string} raw
   * @returns {boolean} true if parsing succeeded
   */
  const handleInputBlur = useCallback(
    (raw) => {
      const parsed = parseTimeInput(raw);
      if (!parsed) return false;
      emit(parsed.h24, parsed.minutes);
      return true;
    },
    [emit]
  );

  // ── popup open/close ──────────────────────────────────────────────────────

  const openPopup = useCallback(() => setIsOpen(true), []);
  const closePopup = useCallback(() => setIsOpen(false), []);
  const togglePopup = useCallback(() => setIsOpen((o) => !o), []);

  // Click-outside closes the popup
  useEffect(() => {
    if (!isOpen) return;

    const handleDocClick = (e) => {
      if (wrapperRef.current && wrapperRef.current.contains(e.target)) return;
      if (popupRef.current && popupRef.current.contains(e.target)) return;
      setIsOpen(false);
    };

    document.addEventListener('mousedown', handleDocClick);
    document.addEventListener('touchstart', handleDocClick);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
      document.removeEventListener('touchstart', handleDocClick);
    };
  }, [isOpen]);

  return {
    // display values
    h12,
    minutes,
    ampm,
    isOpen,
    // refs
    wrapperRef,
    popupRef,
    // handlers
    incrementHour,
    decrementHour,
    incrementMinute,
    decrementMinute,
    toggleAmPm,
    handleInputBlur,
    openPopup,
    closePopup,
    togglePopup,
    // formatted string for the input
    formattedValue: formatTime(value),
  };
};

export default useTimePicker;
